import { useRef, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { Box, Col, Row, SimpleCheckbox, SingleCheckbox, Text } from '@opendoor/bricks-next';
import {
  OdProtosCustomerV1Data_Medium as FeedMedium,
  OdProtosCustomerV1Data_SubscriptionStatus as SubscriptionStatus,
} from '__generated__/globalTypes';
import { TextButton } from 'platform/bricks-next';
import type { TextInputSize } from 'platform/components/AccountPreferences';
import { SectionHeader } from 'platform/components/AccountPreferences/SectionHeader';
import { useAuthAuthenticated } from 'platform/components/AuthContext';
import type { CommsPreferencesQuery_customerCommPreferences_OdProtosCustomerV1Services_CustomerCommPreferences_Query_getCommPreference_OdProtosCustomerV1Services_GetCommPreferenceResponse_commPreference_OdProtosCustomerV1Data_CommPreference_subscriptions_OdProtosCustomerV1Data_Subscription as Subscription } from 'platform/graphql/__generated__/useCommsPreferences';
import type { CustomerDetailsQuery_customerService_OdProtosCustomerV1Services_Customer_Query_getCurrentCustomer_OdProtosCustomerV1Services_CustomerResponses_GetCurrentCustomer_customer_OdProtosCustomerV1Data_Customer as Customer } from 'platform/graphql/__generated__/useCustomerDetails';
import {
  useSubscribeToFeed,
  useUnSubscribeAllFeed,
  useUnSubscribeToFeed,
} from 'platform/graphql/useCommsPreferences';
import { useObservability } from 'platform/observability';
import { Divider } from 'shared/components/Divider';
import type { ShowToastFn } from 'shared/hooks/useToast';

enum SubscriptionFeed {
  MarketingFeed = 'Marketing Feed',
}

interface FeedSubscriptionState {
  [FeedMedium.EMAIL]: boolean;
  [FeedMedium.SMS]: boolean;
}

interface CommunicationPreferences {
  marketingFeed: FeedSubscriptionState;
}

const getFeedSubscriptions = (subscriptions: Subscription[] = []): CommunicationPreferences => {
  const marketingSubscriptions = subscriptions.filter(
    (subscription) => subscription.feed?.name === SubscriptionFeed.MarketingFeed,
  );

  return {
    marketingFeed: {
      [FeedMedium.EMAIL]:
        marketingSubscriptions.find((subscriptions) => subscriptions.medium === FeedMedium.EMAIL)
          ?.status === SubscriptionStatus.SUBSCRIBED,
      [FeedMedium.SMS]:
        marketingSubscriptions.find((subscriptions) => subscriptions.medium === FeedMedium.SMS)
          ?.status === SubscriptionStatus.SUBSCRIBED,
    },
  };
};

/**
 * Displays Communication preferences and allows the user to edit it.
 *
 */
export const CommsPreferencesContainer = ({
  subscriptions,
  inputSize,
  customerInfo,
  showToast,
}: {
  subscriptions?: Subscription[];
  inputSize: TextInputSize;
  customerInfo?: Customer | null;
  showToast: ShowToastFn;
}) => {
  const initialSubscriptionState = getFeedSubscriptions(subscriptions);

  const { customerUUID } = useAuthAuthenticated();

  const { subscribeToFeed, loading: subscribing } = useSubscribeToFeed();

  const { unSubscribeToFeed, loading: unSubscribing } = useUnSubscribeToFeed();

  const { unSubscribeAllFeed, loading: unSubscribingAllFeed } = useUnSubscribeAllFeed();

  const { trackError } = useObservability();

  const savedFormState = useRef<CommunicationPreferences>(initialSubscriptionState);

  //Subscription States
  const [marketingEmail, setMarketingEmail] = useState<boolean>(
    initialSubscriptionState.marketingFeed[FeedMedium.EMAIL],
  );
  const [marketingSms, setMarketingSms] = useState<boolean>(
    initialSubscriptionState.marketingFeed[FeedMedium.SMS],
  );

  const updateSubscription = (
    feedName: SubscriptionFeed,
    medium: FeedMedium,
    subscribed: boolean,
  ) => {
    if (subscribed) {
      return subscribeToFeed({
        variables: {
          userId: customerUUID,
          feedName,
          medium,
        },
      });
    }

    return unSubscribeToFeed({
      variables: {
        userId: customerUUID,
        feedName,
        medium,
      },
    });
  };

  const showErrorToast = () => {
    showToast({
      type: 'error',
      message: 'There was an error updating your communication preferences, please try again.',
      analyticsName: 'cake-comms-prefs-error-toast',
    });
  };

  const updateCommsPreferences = async () => {
    const updatedCommsPreferences: CommunicationPreferences = {
      marketingFeed: {
        [FeedMedium.EMAIL]: marketingEmail,
        [FeedMedium.SMS]: marketingSms,
      },
    };

    const currentCommsPreferences = savedFormState.current;

    const allPromises = [];

    //Marketing Email
    if (
      currentCommsPreferences.marketingFeed[FeedMedium.EMAIL] !==
      updatedCommsPreferences.marketingFeed[FeedMedium.EMAIL]
    ) {
      allPromises.push(
        updateSubscription(
          SubscriptionFeed.MarketingFeed,
          FeedMedium.EMAIL,
          updatedCommsPreferences.marketingFeed[FeedMedium.EMAIL],
        ),
      );
    }

    //Marketing Sms
    if (
      currentCommsPreferences.marketingFeed[FeedMedium.SMS] !==
      updatedCommsPreferences.marketingFeed[FeedMedium.SMS]
    ) {
      allPromises.push(
        updateSubscription(
          SubscriptionFeed.MarketingFeed,
          FeedMedium.SMS,
          updatedCommsPreferences.marketingFeed[FeedMedium.SMS],
        ),
      );
    }

    if (allPromises.length > 0) {
      try {
        const responses = await Promise.all(allPromises);

        let hasError = false;

        responses.forEach((data) => {
          if (data?.error) {
            hasError = true;
          }
        });

        if (hasError) {
          return showErrorToast();
        }

        savedFormState.current = updatedCommsPreferences;
        showToast({
          type: 'success',
          message: 'Your Communication Preferences have been updated',
          analyticsName: 'cake-comms-prefs-success-toast',
        });
      } catch (err) {
        showErrorToast();
        trackError('Error Updating Communication Preferences', err as ErrorLike);
      }
    }
  };

  const optOutOfAllFeed = async () => {
    try {
      const data = await unSubscribeAllFeed({
        variables: { userId: customerUUID },
      });
      if (data?.error) {
        return showErrorToast();
      }
      setMarketingEmail(false);
      setMarketingSms(false);
      const updatedCommsPreferences: CommunicationPreferences = {
        marketingFeed: {
          [FeedMedium.EMAIL]: false,
          [FeedMedium.SMS]: false,
        },
      };
      savedFormState.current = updatedCommsPreferences;
      showToast({
        type: 'success',
        message: 'Your Communication Preferences have been updated',
        analyticsName: 'cake-comms-prefs-success-toast',
      });
    } catch (err) {
      showErrorToast();

      trackError('Error unsubscring from all Marketing content', err as ErrorLike);
    }
  };

  const getDisabledState = (feedName: SubscriptionFeed, feedMedium: FeedMedium) => {
    if (
      feedMedium === FeedMedium.SMS &&
      customerInfo?.primaryPhoneNumber?.number?.value === undefined
    ) {
      return {
        disabled: true,
        subText: 'Enter your phone number to receive text messages from us',
      };
    }

    const isSubscriptionForceUnSubscribed =
      subscriptions?.find(
        (subscription) =>
          subscription.feed?.name === feedName && subscription.medium === feedMedium,
      )?.status === SubscriptionStatus.FORCE_UNSUBSCRIBED;

    if (isSubscriptionForceUnSubscribed) {
      return {
        disabled: true,
        subText: `You have unsubscribed to ${feedMedium} messages from us`,
      };
    }

    return {
      disabled: false,
      subText: '',
    };
  };

  const marketingEmailDisabledState = getDisabledState(
    SubscriptionFeed.MarketingFeed,
    FeedMedium.EMAIL,
  );

  const marketingSmsDisabledState = getDisabledState(
    SubscriptionFeed.MarketingFeed,
    FeedMedium.SMS,
  );

  const getMarketingSubText = () => {
    if (marketingEmailDisabledState.disabled && marketingSmsDisabledState.disabled) {
      return 'You have unsubscribed to text messages and emails from us.';
    }

    if (marketingEmailDisabledState.disabled) {
      return 'You have unsubscribed to emails from us.';
    }

    if (marketingSmsDisabledState.disabled) {
      if (customerInfo?.primaryPhoneNumber?.number?.value === undefined) {
        return 'Enter your phone number to receive text messages from us';
      }

      return 'You have unsubscribed to text messages from us.';
    }

    return '';
  };

  const marketingSubText = getMarketingSubText();

  const DesktopLayout = () => (
    <Col gap="$12x">
      <Row justifyContent="flex-end">
        <Row gap="$20x" paddingRight="$2x">
          <Text tag="span" typography="$labelMedium">
            Email
          </Text>
          <Text tag="span" typography="$labelMedium">
            SMS
          </Text>
        </Row>
      </Row>
      <Col gap="$12x">
        <Row justifyContent="space-between" alignItems="flex-start">
          <Col>
            <Text tag="p" typography="$labelXlarge">
              Marketing communications related to selling
            </Text>
            {marketingSubText && (
              <Text tag="p" typography="$bodySmall" color="$contentTertiary">
                {marketingSubText}
              </Text>
            )}
          </Col>
          <Row gap="$20x">
            <SimpleCheckbox
              id="marketing-email-checkbox"
              checked={marketingEmail}
              disabled={marketingEmailDisabledState?.disabled}
              onPress={(_, checked) => {
                setMarketingEmail(!!checked);
              }}
              value="Marketing email Subscription"
              analyticsName="marketing-email-subscription"
              ariaLabel="Marketing Email Subscription"
              size="medium"
            />
            <SimpleCheckbox
              id="marketing-sms-checkbox"
              /*
              Edge case for a new user who does not have a phone number will
              still have the subscription enabled(on Account creation), in this case we want it to 
              show as unchecked and disabled.
              */
              checked={!marketingSmsDisabledState.disabled && marketingSms}
              disabled={marketingSmsDisabledState.disabled}
              onPress={(_, checked) => {
                setMarketingSms(!!checked);
              }}
              value="Marketing SMS Subscription"
              analyticsName="marketing-sms-subscription"
              ariaLabel="Marketing Sms Subscription"
              size="medium"
            />
          </Row>
        </Row>
      </Col>
      <Divider />
    </Col>
  );

  const MobileLayout = () => (
    <Col gap="$12x">
      <Col gap="$12x">
        <Col>
          <Text tag="p" typography="$labelLarge">
            Marketing communications
          </Text>
          <Text tag="p" typography="$bodySmall">
            Marketing related to selling your home
          </Text>
        </Col>

        <SingleCheckbox
          analyticsName="marketing-email-subscription"
          checked={marketingEmail}
          disabled={marketingEmailDisabledState.disabled}
          onPress={(_, checked) => {
            setMarketingEmail(!!checked);
          }}
          id="marketing-email-checkbox"
          label="Email"
          subtitle={marketingEmailDisabledState.subText}
          size="small"
          value="Marketing Email"
        />

        <SingleCheckbox
          analyticsName="marketing-sms-subscription"
          checked={marketingSms}
          disabled={marketingSmsDisabledState.disabled}
          subtitle={marketingSmsDisabledState.subText}
          onPress={(_, checked) => {
            setMarketingSms(!!checked);
          }}
          id="marketing-sms-checkbox"
          label="SMS"
          size="small"
          value="Marketing SMS"
        />
      </Col>
    </Col>
  );

  return (
    <Box
      testID="contact-info-container"
      gap="$12x"
      $largerThanSM={{
        gap: '$16x',
      }}
    >
      <SectionHeader
        header="Communication preferences"
        saveButtonId="save-comms-preferences"
        analyticsName="save-communication-preferences"
        onSave={() => void updateCommsPreferences()}
        inputSize={inputSize}
        updating={subscribing || unSubscribing || unSubscribingAllFeed}
      />
      <Col $smallerThanMD={{ display: 'none' }}>
        <DesktopLayout />
      </Col>
      <Col $largerThanSM={{ display: 'none' }}>
        <MobileLayout />
      </Col>
      <TextButton
        id="unsubscribe-all-feed"
        onPress={() => void optOutOfAllFeed()}
        disabled={subscribing || unSubscribing || unSubscribingAllFeed}
        marginTop="$8x"
        alignSelf="flex-end"
        analyticsName="unsubscribe-all-marketing-content"
        label="Unsubscribe from all marketing content"
        variant="secondary"
        size="medium"
      />
    </Box>
  );
};
